import * as React from 'react';
import Fragment from 'react-dot-fragment';
import { observer } from 'mobx-react';
import { style } from 'typestyle';
import { colorAmber, colorBrand, colorCrimson, colorDarkGreen, colorWhite } from '../../../theme/color';
import { ScrollableOverlay } from '../../scrollable-overlay/scrollable-overlay';
import { ScrollableOverlayHeaderStyle } from '../../../styles/scrollable-overlay';
import { Logo } from '../../logo';
import { Loading } from '../../loading/loading';
import PrismicDOM from 'prismic-dom';
import { IObservableArray, isObservableArray, isObservableMap } from 'mobx';
import { Component, deps, inject } from '../../../lib/component';
import { TrackSchema } from '../../../types/schema';
import TrackInfo from './track-info';

export interface EulaModalProps {
  show: boolean;
  title: string;
  track: TrackSchema;
  onRequestClose(): void;
  coupon?: any;
  i18n?: {
    title: string;
    strap: any;
    loading: string;
    not_available: string;
    close: string;
    first_discount: string;
    affiliate_discount: string;
  };
}

@inject(deps)
@observer
export class EulaModal extends Component<EulaModalProps> {
  render() {
    const { show, title, onRequestClose, track, model } = this.props;
    const {
      page: { checkout },
      content,
    } = model;

    const trackKey = 'track-info';
    const eulaKey = 'eula-content';

    // Base loading state
    const loading = <Loading key={trackKey} text="Loading license information..." />;

    // Escape early (prior to data wrangling) if overlay isn't due to be shown
    if (!show) {
      return (
        <ScrollableOverlay show={show} onRequestClose={onRequestClose}>
          {loading}
        </ScrollableOverlay>
      );
    }

    const usageInformation = checkout.tracksWithUsage.get(track.identity);
    const eulaContent = content.eulas.get(track.reporting_party.slug === 'umg' ? 'umg' : 'lickd');

    const bodyContent: React.ReactNode[] = [loading];

    // Deal with EULA content first so we know whether to show separate loading states for EULA and track data
    if (eulaContent instanceof Error) {
      bodyContent.push(<div key={eulaKey}>{eulaContent.message}</div>);
    } else if (isObservableArray(eulaContent)) {
      bodyContent.push(
        <div
          key={eulaKey}
          dangerouslySetInnerHTML={{
            __html: PrismicDOM.RichText.asHtml(eulaContent),
          }}
        />,
      );
    }
    const eulaResolved = bodyContent.length === 2;

    // If the EULA content is already resolved, track info needs separate error state
    if (usageInformation instanceof Error && eulaResolved) {
      bodyContent[0] = <div key={trackKey}>{usageInformation.message}</div>;
    } else if (typeof usageInformation === 'object' && usageInformation !== null && 'identity' in usageInformation) {
      bodyContent[0] = <TrackInfo key={trackKey} track={usageInformation} />;
    }
    const trackInfoResolved = bodyContent[0] === loading;

    // As track info is resolved and EULA content hasn't, add separate EULA loading state
    if (trackInfoResolved && !eulaResolved) {
      bodyContent.push(<Loading key={eulaKey} text="Loading EULA..." />);
    }

    return (
      <ScrollableOverlay show={show} onRequestClose={onRequestClose}>
        <header className={style(ScrollableOverlayHeaderStyle, { backgroundColor: colorBrand.toHexString() })}>
          <Logo />
          <h1 className={style({ color: colorWhite.toHexString() })}>{title}</h1>
        </header>
        <main className={ScrollableOverlay.styles.body}>
          <span>{bodyContent}</span>
        </main>
      </ScrollableOverlay>
    );
  }
}
